import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, Link2, Mail, Linkedin, MessageCircle, ShieldAlert } from "lucide-react";
import { Header } from "@/components/Header";
import { useAuth } from "@/context/AuthContext";

const ORIGIN = process.env.REACT_APP_BACKEND_URL;
const VIDEO_URL = `${ORIGIN}/api/media/football-challenge-promo.mp4`;
const THUMB_URL = `${ORIGIN}/api/media/football-challenge-promo.jpg?v=2`;
const SHARE_URL = `${ORIGIN}/api/promo`;
const SUBJECT = "Football Challenge: drive midweek footfall";
const MESSAGE =
  "Football Challenge is a fully managed promotional platform that helps pubs drive midweek footfall and improve ROI. " +
  `Watch the 2 minute overview: ${SHARE_URL}. More at www.amblemead.biz`;

const SHARE_LINKS = [
  { id: "email", label: "Email", icon: Mail,
    href: `mailto:?subject=${encodeURIComponent(SUBJECT)}&body=${encodeURIComponent(MESSAGE)}` },
  { id: "linkedin", label: "LinkedIn", icon: Linkedin,
    href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(SHARE_URL)}` },
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircle,
    href: `https://wa.me/?text=${encodeURIComponent(MESSAGE)}` },
];

const BTN =
  "inline-flex items-center gap-2 text-sm font-medium px-4 py-2.5 rounded-sm border transition-colors duration-200 active:scale-[0.98]";
const BTN_OUTLINE = `${BTN} border-zinc-200 bg-white text-zinc-900 hover:border-zinc-950`;

async function copyLink() {
  try {
    await navigator.clipboard.writeText(SHARE_URL);
  } catch {
    const ta = document.createElement("textarea");
    ta.value = SHARE_URL;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    ta.remove();
  }
  toast.success("Link copied");
}

function NotAuthorised() {
  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-6xl mx-auto px-6 sm:px-10 py-16" data-testid="promo-video-not-authorised">
        <ShieldAlert className="h-6 w-6 text-red-600" />
        <h1 className="mt-3 font-heading text-3xl font-bold tracking-tight text-zinc-950">Not authorised.</h1>
        <p className="mt-2 text-sm text-zinc-600">This page is for Super-Admins only.</p>
      </main>
    </div>
  );
}

function ShareActions() {
  return (
    <div className="mt-6 space-y-3">
      <div className="flex flex-wrap gap-3">
        <a href={`${VIDEO_URL}?download=1`} download="football-challenge-promo.mp4" data-testid="promo-video-download-btn"
          className={`${BTN} border-zinc-950 bg-zinc-950 text-white hover:bg-zinc-800`}>
          <Download className="h-4 w-4" /> Download video
        </a>
        <button type="button" onClick={copyLink} data-testid="promo-video-copy-link-btn" className={BTN_OUTLINE}>
          <Link2 className="h-4 w-4" /> Copy link
        </button>
        {SHARE_LINKS.map(({ id, label, icon: Icon, href }) => (
          <a key={id} href={href} target={id === "email" ? undefined : "_blank"} rel="noopener noreferrer"
            data-testid={`promo-video-share-${id}`} className={BTN_OUTLINE}>
            <Icon className="h-4 w-4" /> {label}
          </a>
        ))}
      </div>
      <p className="text-xs text-zinc-500" data-testid="promo-video-tip">
        Tip: download the file to attach it directly to an email, or send the link.
      </p>
    </div>
  );
}

export default function PromoVideoPage() {
  const { user, bootstrapped } = useAuth();
  if (!bootstrapped) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <NotAuthorised />;
  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-6xl mx-auto px-6 sm:px-10 py-8 sm:py-10 space-y-8" data-testid="promo-video-page">
        <header className="pb-4 border-b border-zinc-200">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Marketing</p>
          <h1 className="mt-1 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-zinc-950">Promo video.</h1>
          <p className="mt-2 text-sm text-zinc-600 max-w-xl">
            Share this with pub managers, area managers and marketing teams.
          </p>
        </header>
        <section className="border border-zinc-200 rounded-sm bg-white p-5 sm:p-6" data-testid="promo-video-card">
          <div className="max-w-[960px]">
            <video data-testid="promo-video-player" controls preload="metadata" playsInline poster={THUMB_URL}
              src={VIDEO_URL} className="w-full aspect-video bg-black rounded-sm" />
            <p className="mt-3 text-xs text-zinc-500" data-testid="promo-video-details">
              MP4, 1280x720, 2:17, about 9.7 MB
            </p>
            <ShareActions />
          </div>
        </section>
      </main>
    </div>
  );
}
